import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiConfig';

/**
 * Magic-login landing page.
 * Exchanges ?token=... for an access_token, stores it, and navigates to /dashboard.
 */
export default function MagicLogin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setErrorMsg(t('auth.errors.magic_link_invalid'));
      return;
    }
    (async () => {
      try {
        // Decode JWT-look check: 3 dot-separated parts → already a JWT (new flow).
        // Otherwise it's a single-use magic token → exchange it via POST.
        const looksLikeJwt = token.split('.').length === 3;
        let accessToken = null;

        if (looksLikeJwt) {
          accessToken = token;
        } else {
          const res = await axios.post(`${API}/auth/magic-login`, { token });
          accessToken = res.data?.access_token;
        }

        if (accessToken) {
          await loginWithToken(accessToken);
          setStatus('success');
          const next = params.get('next');
          const target = next === 'orb' ? '/orb-app' : '/dashboard';
          setTimeout(() => navigate(target), 600);
        } else {
          setStatus('error');
          setErrorMsg(t('auth.errors.magic_link_invalid'));
        }
      } catch (e) {
        const code = e.response?.data?.detail;
        const key = `auth.errors.${code}`;
        const tx = t(key);
        setStatus('error');
        setErrorMsg(tx !== key ? tx : t('auth.errors.magic_link_invalid'));
      }
    })();
  }, [params, navigate, t, loginWithToken]);

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        {status === 'loading' && (
          <>
            <Loader2 size={36} className="spin" color="#00d4ff" />
            <h2 style={styles.title}>{t('auth.magic.verifying')}</h2>
            <p style={styles.subtitle}>{t('auth.magic.verifyingText')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle size={36} color="#22c55e" />
            <h2 style={styles.title}>{t('auth.magic.welcome')}</h2>
            <p style={styles.subtitle}>{t('auth.magic.redirecting')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle size={36} color="#ef4444" />
            <h2 style={styles.title}>{t('auth.magic.failed')}</h2>
            <p style={styles.subtitle}>{errorMsg}</p>
            <button
              onClick={() => navigate('/login')}
              style={styles.button}
              data-testid="magic-back-to-login"
            >
              {t('auth.magic.backToLogin')}
            </button>
          </>
        )}
      </div>
      <style>{`
        .spin { animation: mspin 1.1s linear infinite; }
        @keyframes mspin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)', padding: 20,
  },
  card: {
    background: 'rgba(15,23,42,0.8)',
    border: '1px solid rgba(0,212,255,0.25)', borderRadius: 20,
    padding: '32px 28px', textAlign: 'center', maxWidth: 420, width: '100%',
    color: '#f8fafc', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 14,
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  title: { margin: 0, fontSize: 22, fontWeight: 700 },
  subtitle: { margin: 0, fontSize: 14, color: '#94a3b8' },
  button: {
    marginTop: 10, padding: '10px 22px', borderRadius: 10,
    background: 'linear-gradient(135deg,#00d4ff,#0ea5e9)',
    border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer',
  },
};
